import { FC, FormEvent, useEffect, useRef, useState } from "react";

import { SearchIcon } from "@/assets/icons";
import { useChatSearch } from "@/hooks/agenticAI/useChatSearch";
import { useRecentChatSearches } from "@/hooks/agenticAI/useRecentChatSearches";
import { useLoadMoreOnVisible } from "@/hooks/useLoadMoreOnVisible";

import { twMerge } from "tailwind-merge";

import { ChatListSkeletonRow } from "../ChatListLoadMoreFooter";
import {
  AGENTIC_AI_CHAT_HISTORY_RETRY,
  AGENTIC_AI_SEARCH_DESCRIPTION,
  AGENTIC_AI_SEARCH_ERROR,
  AGENTIC_AI_SEARCH_ERROR_STATUS,
  AGENTIC_AI_SEARCH_HEADING,
  AGENTIC_AI_SEARCH_NO_RESULTS_STATUS,
  AGENTIC_AI_SEARCH_PLACEHOLDER,
  type ChatListLoadMore
} from "../constants";
import { ChatSearchIdle, ChatSearchNoResults } from "./ChatSearchEmptyStates";
import ChatSearchResults from "./ChatSearchResults";
import { formatResultCount } from "./formatResultCount";

const SKELETON_ROW_COUNT = 3;

type Props = {
  isSidebarOpen: boolean;
  onSelectChat: (threadId: string) => void;
};

const AgenticAISearchPage: FC<Props> = ({ isSidebarOpen, onSelectChat }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [input, setInput] = useState("");

  const {
    query,
    isLoading,
    results,
    total,
    isTotalExact,
    isInitialError,
    isLoadMoreError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch
  } = useChatSearch(input);
  const recentSearches = useRecentChatSearches(query);
  const loadMore: ChatListLoadMore = {
    ref: useLoadMoreOnVisible({
      hasNextPage,
      isFetchingNextPage,
      isPaused: isLoadMoreError,
      fetchNextPage
    }),
    isFetching: isFetchingNextPage,
    isError: isLoadMoreError,
    onRetry: () => void fetchNextPage()
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
  };

  const handlePickRecent = (term: string) => {
    setInput(term);
    inputRef.current?.focus();
  };

  const resultTotal = total ?? results.length;

  const getAnnouncement = () => {
    if (query === "" || isLoading) return "";
    if (isInitialError) return AGENTIC_AI_SEARCH_ERROR_STATUS;
    if (results.length === 0)
      return `${AGENTIC_AI_SEARCH_NO_RESULTS_STATUS} "${query}"`;

    return `${formatResultCount(resultTotal, isTotalExact)} for "${query}"`;
  };

  const renderBody = () => {
    if (query === "") {
      return (
        <ChatSearchIdle
          recentSearches={recentSearches}
          onPickRecent={handlePickRecent}
        />
      );
    }

    if (isLoading) {
      return (
        <ul className="flex flex-col overflow-hidden rounded-xl border border-gainsboro bg-white">
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <ChatListSkeletonRow key={index} />
          ))}
        </ul>
      );
    }

    if (isInitialError) {
      return (
        <div className="flex flex-col items-start gap-1 text-sm text-txtBody">
          <p>{AGENTIC_AI_SEARCH_ERROR}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="font-semibold text-teal-500 hover:underline"
          >
            {AGENTIC_AI_CHAT_HISTORY_RETRY}
          </button>
        </div>
      );
    }

    if (results.length === 0) return <ChatSearchNoResults query={query} />;

    return (
      <ChatSearchResults
        query={query}
        total={resultTotal}
        isTotalExact={isTotalExact}
        rows={results}
        onSelect={onSelectChat}
        loadMore={loadMore}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white-smoke px-7 pb-10 hd:pl-[92px] hd:pr-[132px]">
      <div
        className={twMerge(
          "flex w-full flex-col gap-5",
          // Same height as the header row shown above this page when the sidebar is hidden.
          isSidebarOpen && "pt-[calc(2.1rem+28px)]"
        )}
      >
        <header className="flex flex-col gap-1">
          <h1 className="text-[28px] font-bold leading-9 text-txtHeading">
            {AGENTIC_AI_SEARCH_HEADING}
          </h1>
          <p className="text-base text-txtBody">
            {AGENTIC_AI_SEARCH_DESCRIPTION}
          </p>
        </header>

        <form
          role="search"
          aria-label={AGENTIC_AI_SEARCH_HEADING}
          onSubmit={handleSubmit}
        >
          <div className="flex items-center gap-4 rounded-xl border border-gainsboro bg-white p-4 focus-within:border-teal-500">
            <SearchIcon aria-hidden className="size-4 shrink-0 fill-dim-gray" />
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              aria-label={AGENTIC_AI_SEARCH_HEADING}
              placeholder={AGENTIC_AI_SEARCH_PLACEHOLDER}
              className="min-w-0 flex-1 bg-transparent text-sm leading-[18px] text-txtHeading outline-none placeholder:text-txtPlaceholder"
            />
          </div>
        </form>

        <output className="sr-only">{getAnnouncement()}</output>

        <div aria-busy={isLoading}>{renderBody()}</div>
      </div>
    </div>
  );
};

export default AgenticAISearchPage;
